import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';
import BasicInput from '@/components/BasicInput';
import Select from '@/components/Select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipArrow } from '@/components/Tooltip';
import { getCardBrand } from '@/utils/cardUtils';
import { validateCardNumber, validateCardHolder, validateExpiryDate, validateCVV } from '@/utils/cardValidation';
import markedIcon from '@/assets/marked.svg';
import questionIcon from '@/assets/question_grey.svg';
import visaLogo from '@/assets/cards/visa.svg';
import mastercardLogo from '@/assets/cards/mastercard.svg';
import dinersLogo from '@/assets/cards/diners.svg';
import amexLogo from '@/assets/cards/amex.svg';
import jcbLogo from '@/assets/cards/jcb.svg';
import { trackEvent } from '@/utils/googleAnalytics';
import cvvTipImage from '@/assets/cvv_tip.jpg';

const PaymentSection = ({
  onComplete,
  onEditingChange,
  onEditRequest,
  isRequired,
  hasPaymentMethods = false,
  paymentCards = [],
  finalSelectedCardId,
  isCompactLayout = false,
}) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardHolder: '',
    expiry: '',
    cvc: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('credit');

  // Handle finalSelectedCardId from parent (similar to AddressSection)
  useEffect(() => {
    if (finalSelectedCardId) {
      setSelectedCardId(finalSelectedCardId);
      setIsEditing(false);
    }
  }, [finalSelectedCardId]);

  // Auto-select default or first card if cards exist and update editing state
  useEffect(() => {
    if (paymentCards.length > 0) {
      // Cards exist - auto-select if none selected
      const selectedCard = paymentCards.find(c => c.id == selectedCardId);
      if (!selectedCard) {
        const defaultCard = paymentCards.find(c => c.isDefault) || paymentCards[0];
        setSelectedCardId(defaultCard.id);
      }

      // Always exit editing mode when cards exist (auto-complete should handle the rest)
      setIsEditing(false);
      setIsCreatingNew(false);
    }
  }, [paymentCards]);

  // Auto-populate form data when a card is selected (similar to AddressSection)
  useEffect(() => {
    if (paymentCards.length > 0 && selectedCardId) {
      const selectedCard = paymentCards.find(c => c.id === selectedCardId);
      if (selectedCard) {
        setFormData({
          cardNumber: selectedCard.number,
          cardHolder: selectedCard.holderName,
          expiry: selectedCard.expiryDate,
          cvc: ''
        });
        setIsCreatingNew(false);
      }
    }
  }, [paymentCards, selectedCardId]);

  // Auto-complete when a card is selected and not creating new
  useEffect(() => {
    if (selectedCardId && !isCreatingNew && paymentCards.length > 0) {
      const selectedCard = paymentCards.find(c => c.id === selectedCardId);
      if (selectedCard) {
        // console.log('Auto-completing payment with card:', selectedCard);
        // Use a small timeout to ensure state updates are complete
        const timer = setTimeout(() => {
          onComplete({
            type: 'SAVED_CREDIT_CARD',
            cardId: selectedCardId,
            selectedCard: selectedCard
          });
        }, 0);

        return () => clearTimeout(timer);
      }
    }
  }, [selectedCardId, isCreatingNew, paymentCards, onComplete]);

  // Only force editing mode if explicitly required and no cards exist
  useEffect(() => {
    if (isRequired && paymentCards.length === 0) {
      setIsEditing(true);
      setIsCreatingNew(true);
    }
  }, [isRequired, paymentCards]);

  // Also notify parent when editing state changes internally
  useEffect(() => {
    onEditingChange && onEditingChange(isEditing);
  }, [isEditing, onEditingChange]);



  // Get card brand icon
  const getCardBrandIcon = (brand) => {
    const iconMap = {
      visa: visaLogo,
      mastercard: mastercardLogo,
      amex: amexLogo,
      diners: dinersLogo,
      jcb: jcbLogo,
    };
    return iconMap[brand] || mastercardLogo; // Default to mastercard
  };

  // Create options for card selection
  const cardOptions = [
    ...(paymentCards.map(card => ({
      value: card.id,
      label: `**** ${card.number.slice(-4)}`,
      card: card
    })) || []),
    {
      value: 'new',
      label: t('purchase.payment.add_new_card')
    }
  ];

  // Handle card selection
  const handleCardSelect = (value) => {
    // Clear any validation errors
    setValidationError('');
    setFieldErrors({});

    if (value === 'new') {
      setIsCreatingNew(true);
      setSelectedCardId(null);
      setFormData({
        cardNumber: '',
        cardHolder: '',
        expiry: '',
        cvc: ''
      });
    } else {
      setIsCreatingNew(false);
      setSelectedCardId(value);
      const selectedCard = paymentCards.find(c => c.id === value);
      if (selectedCard) {
        setFormData({
          cardNumber: selectedCard.number,
          cardHolder: selectedCard.holderName,
          expiry: selectedCard.expiryDate,
          cvc: ''
        });
      }
    }
  };

  // Get selected card data
  const selectedCard = paymentCards.find(c => c.id === selectedCardId);

  const handleInputChange = (field, value) => {
    if (validationError) {
      setValidationError('');
    }

    // Clear field-specific errors
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }

    let formattedValue = value;

    // Format and validate based on field type
    if (field === 'cardNumber') {
      // Remove all non-digits
      const cleanValue = value.replace(/\D/g, '');
      // Limit to 19 digits maximum
      const limitedValue = cleanValue.slice(0, 19);
      // Add spaces every 4 digits for display
      formattedValue = limitedValue.replace(/(\d{4})(?=\d)/g, '$1 ');
    } else if (field === 'cardHolder') {
      // Limit to 29 characters (less than 30)
      formattedValue = value.slice(0, 29);
      // Only allow English letters, spaces, hyphens, and apostrophes
      formattedValue = formattedValue.replace(/[^A-Za-z\s\-']/g, '');
    } else if (field === 'expiry') {
      // Remove any existing slashes and non-digits
      const cleanValue = value.replace(/\D/g, '');
      // Limit to 4 digits
      const limitedValue = cleanValue.slice(0, 4);

      // Only add slash when user inputs the 3rd digit, or if there are already 4 digits
      if (limitedValue.length >= 3) {
        formattedValue = limitedValue.slice(0, 2) + '/' + limitedValue.slice(2, 4);
      } else {
        // For 1-2 digits, just show the digits without slash
        formattedValue = limitedValue;
      }
    } else if (field === 'cvc') {
      // Remove all non-digits and limit to 4 digits
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setFormData(prev => ({ ...prev, [field]: formattedValue }));
  };

  // Check if form is valid - computed value that updates when dependencies change
  const isFormValid = useMemo(() => {
    // For saved cards, we just need the card to be selected
    if (!isCreatingNew) {
      return selectedCardId !== null;
    }

    // For new cards, first check if all required fields have content
    const hasAllFields = formData.cardNumber?.trim() &&
                        formData.cardHolder?.trim() &&
                        formData.expiry?.trim() &&
                        formData.cvc?.trim();

    if (!hasAllFields) {
      return false;
    }

    // Then validate all fields
    const cardNumberValid = validateCardNumber(formData.cardNumber).isValid;
    const cardHolderValid = validateCardHolder(formData.cardHolder).isValid;
    const expiryValid = validateExpiryDate(formData.expiry).isValid;
    const cvvValid = validateCVV(formData.cvc).isValid;

    return cardNumberValid && cardHolderValid && expiryValid && cvvValid;
  }, [isCreatingNew, selectedCardId, formData.cardNumber, formData.cardHolder, formData.expiry, formData.cvc]);

  const handleRegister = () => {
    trackEvent('creditcard_save_click');

    // For saved cards, just proceed
    if (!isCreatingNew && selectedCardId) {
      setIsEditing(false);
      onComplete({
        type: 'SAVED_CREDIT_CARD',
        cardId: selectedCardId,
        selectedCard: selectedCard
      });
      return;
    }

    // For new cards, validate all fields
    const cardNumberResult = validateCardNumber(formData.cardNumber);
    const cardHolderResult = validateCardHolder(formData.cardHolder);
    const expiryResult = validateExpiryDate(formData.expiry);
    const cvvResult = validateCVV(formData.cvc);

    const errors = {};
    if (!cardNumberResult.isValid) errors.cardNumber = cardNumberResult.error;
    if (!cardHolderResult.isValid) errors.cardHolder = cardHolderResult.error;
    if (!expiryResult.isValid) errors.expiry = expiryResult.error;
    if (!cvvResult.isValid) errors.cvc = cvvResult.error;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setValidationError(t('purchase.payment.validation_error'));
      return;
    }

    // Clear any previous errors
    setFieldErrors({});
    setValidationError('');
    setIsEditing(false);

    // Pass payment data to parent
    onComplete({
      type: 'TEMPORARY_CREDIT_CARD',
      ...formData
    });
  };

  if (!isEditing) {
    if (hasPaymentMethods || selectedCard || (isCreatingNew && formData.cardNumber)) {
      const displayCard = selectedCard || {
        number: formData.cardNumber,
        holderName: formData.cardHolder,
        brand: getCardBrand(formData.cardNumber)
      };

      // Extract last 4 digits for display
      const lastFourDigits = displayCard.number ? displayCard.number.slice(-4) : '';

      return (
        <div className="bg-white rounded-md">
          <div className={`${isCompactLayout ? "pt-6" : "py-6"} px-6 flex justify-between items-center`}>
            <div className="flex items-center justify-center gap-2">
              <h2 className={`${isCompactLayout ? "text-sm" : "text-lg"} font-bold`}>{t('purchase.payment.title')}</h2>
              <img src={markedIcon} className="w-5 h-5" alt="" />
            </div>
            <button
              onClick={() => {
                if (onEditRequest && !onEditRequest()) {
                  return; // Don't allow edit if other section is editing
                }
                setIsEditing(true);
              }}
              className="px-3.5 py-1 rounded-3xl border border-[#111111] text-xs"
            >
              {t('purchase.payment.edit')}
            </button>
          </div>
          <div className={`px-6 ${isCompactLayout || "pb-6"}`}>
            <div className="w-[190px] py-4 flex flex-col justify-start items-start gap-1.5">
              <div className="w-11 h-8 relative rounded outline outline-1 outline-offset-[-1px] outline-[#eeeeee] overflow-hidden">
                <div className="w-11 h-8 left-0 top-0 absolute overflow-hidden flex items-center justify-center">
                  <img
                    src={getCardBrandIcon(getCardBrand(displayCard.number))}
                    alt={getCardBrand(displayCard.number)}
                    className="h-5"
                  />
                </div>
              </div>
              <div className="self-stretch inline-flex justify-start items-center gap-[7px]">
                <div className="justify-center text-[#111111] text-sm font-normal leading-[21px]">
                  ****{lastFourDigits}
                </div>
              </div>
              <div className="self-stretch justify-center text-black text-sm font-normal">
                {displayCard.holderName}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="bg-white rounded-md">
          <div className="py-6 px-6 flex justify-between items-center border-b border-[#f4f4f4]">
            <h2 className="text-lg font-bold">{t('purchase.payment.title')}</h2>
            <div className="flex items-center gap-2">
              <img src={visaLogo} alt="Visa" className="h-5" />
              <img src={mastercardLogo} alt="Mastercard" className="h-5" />
              <img src={amexLogo} alt="American Express" className="h-5" />
            </div>
          </div>
        </div>
      );
    }
  }

  // Otherwise, show the editing view
  return (
    <div className="bg-white rounded-md overflow-hidden">
      <div className="py-6 px-6 flex items-center">
        <h2 className={`${isCompactLayout ? "text-sm" : "text-lg"} font-bold text-[#111111]`}>{t('purchase.payment.title')}</h2>
      </div>

      <div className="px-6 pb-6">
        {paymentCards.length > 0 && (
          <div className="space-y-3">
            <div className="text-sm font-bold text-[#111111] mb-3">{t('purchase.payment.saved_cards')}</div>
            <div className="pb-6">
              <Select
                options={cardOptions}
                value={isCreatingNew ? 'new' : selectedCardId}
                onValueChange={handleCardSelect}
                className="w-full h-12 rounded-md text-start"
                placeholder={t('purchase.payment.select_card')}
              />
            </div>

            {/* Show selected card information */}
            {selectedCard && !isCreatingNew && (
              <div className="space-y-4">
                <div className="w-[190px] py-4 flex flex-col justify-start items-start gap-1.5">
                  <div className="w-11 h-8 relative rounded outline outline-1 outline-offset-[-1px] outline-[#eeeeee] overflow-hidden">
                    <div className="w-11 h-8 left-0 top-0 absolute overflow-hidden flex items-center justify-center">
                      <img
                        src={getCardBrandIcon(getCardBrand(selectedCard.number))}
                        alt={getCardBrand(selectedCard.number)}
                        className="h-5"
                      />
                    </div>
                  </div>
                  <div className="self-stretch inline-flex justify-start items-center gap-[7px]">
                    <div className="justify-center text-[#111111] text-sm font-normal leading-[21px]">
                      ****{selectedCard.number.slice(-4)}
                    </div>
                  </div>
                  <div className="self-stretch justify-center text-black text-sm font-normal">
                    {selectedCard.holderName}
                  </div>
                </div>

                {/* Save Button for selected card */}
                <div className="flex justify-end">
                  <Button
                    onClick={handleRegister}
                    variant="primary"
                    size={isCompactLayout ? "m" : "l"}
                    className="h-12 px-6 bg-[#111111] text-white rounded-3xl"
                  >
                    {t('purchase.payment.save')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {isCreatingNew && (
        <div className="flex flex-col gap-6">
          {/* Payment Method Selector */}
          <div className="h-12 bg-[#f4f4f4] rounded-md flex overflow-hidden">
            <button 
              className={`flex-1 ${
                paymentMethod === 'credit' ? 'bg-white rounded m-[3px]' : ''
              }`}
              onClick={() => setPaymentMethod('credit')}
            >
              <div className="h-full flex justify-center items-center gap-2">
                <span className="text-sm text-[#111111]">{t('purchase.payment.credit_card')}</span>
              </div>
            </button>
          </div>

          <div className="space-y-4 mt-2">
            {/* Credit Card Icons */}
            <div>
              <div className="text-sm text-[#111111]">{t('purchase.payment.supported_cards')}</div>
              <div className="flex items-center gap-4 mt-2">
                <img src={visaLogo} alt="Visa" className="h-5" />
                <img src={mastercardLogo} alt="Mastercard" className="h-5" />
                <img src={dinersLogo} alt="Diners Club" className="h-5" />
                <img src={amexLogo} alt="American Express" className="h-5" />
                <img src={jcbLogo} alt="JCB" className="h-5" />
              </div>
            </div>

            <div className={isCompactLayout ? 'flex flex-col gap-4' : 'flex gap-3'}>
              {/* Card Number */}
              <div className={isCompactLayout ? 'flex flex-col gap-2 w-full' : 'flex flex-col gap-2 flex-1'}>
                <label className="text-sm text-[#111111]">{t('purchase.payment.card_number')}</label>
                <BasicInput
                  placeholder={t('purchase.payment.card_number_placeholder')}
                  value={formData.cardNumber}
                  onChange={(e) => handleInputChange('cardNumber', e.target.value)}
                  className={`bg-[#f7f7f7] ${fieldErrors.cardNumber ? 'border-red-500' : ''}`}
                  maxLength={23} // 19 digits + 4 spaces
                />
                {fieldErrors.cardNumber && (
                  <span className="text-red-500 text-xs">{fieldErrors.cardNumber}</span>
                )}
              </div>

              {/* Cardholder Name */}
              <div className={isCompactLayout ? 'flex flex-col gap-2 w-full' : 'flex flex-col gap-2 flex-1'}>
                <label className="text-sm text-[#111111]">{t('purchase.payment.card_holder')}</label>
                <BasicInput
                  placeholder={t('purchase.payment.card_holder_placeholder')}
                  value={formData.cardHolder}
                  onChange={(e) => handleInputChange('cardHolder', e.target.value)}
                  className={`bg-[#f7f7f7] ${fieldErrors.cardHolder ? 'border-red-500' : ''}`}
                  maxLength={29}
                />
                {fieldErrors.cardHolder && (
                  <span className="text-red-500 text-xs">{fieldErrors.cardHolder}</span>
                )}
              </div>
            </div>

            {/* Expiry and CVC */}
            <div className={isCompactLayout ? 'flex flex-col gap-4' : 'flex gap-4'}>
              <div className={isCompactLayout ? 'flex flex-col gap-2 w-full' : 'flex-1 flex flex-col gap-2'}>
                <label className="text-sm text-[#111111]">{t('purchase.payment.expiry_date')}</label>
                <BasicInput
                  placeholder={t('purchase.payment.expiry_date_placeholder')}
                  value={formData.expiry}
                  onChange={(e) => handleInputChange('expiry', e.target.value)}
                  className={`bg-[#f7f7f7] ${fieldErrors.expiry ? 'border-red-500' : ''}`}
                  maxLength={5}
                />
                {fieldErrors.expiry && (
                  <span className="text-red-500 text-xs">{fieldErrors.expiry}</span>
                )}
              </div>
              <div className={isCompactLayout ? 'flex flex-col gap-2 w-full' : 'flex-1 flex flex-col gap-2'}>
                <div className="flex items-center justify-between">
                  <label className="text-sm text-[#111111]">{t('purchase.payment.security_code')}</label>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-xs text-[#aaaaaa] flex items-center gap-1">
                        <img src={questionIcon} alt="Help" className="w-4 h-4" />
                        <span>{t('purchase.payment.security_code_help')}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      className="w-[308px] max-w-[308px] bg-black/90 text-white p-3 rounded-md"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div
                          className="text-sm font-normal"
                          dangerouslySetInnerHTML={{ __html: t('purchase.payment.security_code_tooltip') }}
                        />
                        <img
                          src={cvvTipImage}
                          alt="CVV Security Code"
                          className="w-[169px] h-[126px] object-contain"
                        />
                      </div>
                      <TooltipArrow className="fill-black/90" />
                    </TooltipContent>
                  </Tooltip>
                </div>
                <BasicInput
                  placeholder={t('purchase.payment.security_code_placeholder')}
                  value={formData.cvc}
                  onChange={(e) => handleInputChange('cvc', e.target.value)}
                  className={`bg-[#f7f7f7] ${fieldErrors.cvc ? 'border-red-500' : ''}`}
                  maxLength={4}
                />
                {fieldErrors.cvc && (
                  <span className="text-red-500 text-xs">{fieldErrors.cvc}</span>
                )}
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            {validationError && (
              <div className="flex-1 text-red-500 text-sm mt-2">
                {validationError}
              </div>
            )}
            <Button
              onClick={handleRegister}
              variant="primary"
              size={isCompactLayout ? "m" : "l"}
              disabled={!isFormValid}
              className="h-12 px-6 bg-[#111111] text-white rounded-3xl disabled:bg-[#eeeeee] disabled:text-[#aaaaaa]"
            >
              {t('purchase.payment.save')}
            </Button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSection; 
